import {MetricParams, RepChartPoint} from '@store/@types/metric';
import {Map} from 'immutable';
import User, {UserId} from 'records/User';
import {KpiTarget, KpiTargetId} from '@store/@types/kpi';
import {UserSkillBreakdown} from 'records/UserSkillBreakdown';
import CoachingPlan, {PlanId} from 'records/CoachingPlan';
import uniq from 'lodash/uniq';
import isEmpty from 'lodash/isEmpty';
import {getAxisValue} from '@store/metrics-trends/metrics-trends.utils';
import {sumBy} from 'utils/sum';
import {getLatest, getRange} from 'components/dashboard/manager/nine-box/GraphRange.utils';
import {axisLabel, dropNulls} from 'components/dashboard/manager/nine-box/NineBox.utils';
import {MetricsStoreState} from '@store/metrics-trends/NineBoxMetricStore';

export function getRepCoachingDuration(metricData: MetricsStoreState, repId: UserId) {
  const data = [
    ...(metricData.time.meeting[repId]?.data || []),
    ...(metricData.time.review[repId]?.data || []),
  ];
  return sumBy(data, d => d.value);
}

export function getChartData(
  metricData: MetricsStoreState,
  users: Map<UserId, User>,
  kpisById: Map<KpiTargetId, KpiTarget>,
  skillBreakdowns: Map<UserId, UserSkillBreakdown>,
  planMap: Map<PlanId, CoachingPlan>,
  titleMap: Map<MetricParams, string>,
  managerId: UserId
): RepChartPoint[] {
  const repIds = uniq([
    ...Object.keys(metricData.horizontal.data),
    ...Object.keys(metricData.vertical.data),
  ]);
  const {horizontal, vertical} = metricData;
  if (isEmpty(horizontal.data) || isEmpty(vertical.data)) {
    return [];
  }
  const teamRange = getRange(horizontal, vertical, kpisById);
  if (!teamRange) return []; // shouldn't be possible because of above check, alas

  return (
    repIds
      .filter(repId => users.has(repId) && repId !== managerId)
      .map(repId => {
        const rep = users.get(repId)!;
        const horizontalData = dropNulls(horizontal, rep.id);
        const verticalData = dropNulls(vertical, rep.id);
        if (isEmpty(horizontalData) || isEmpty(verticalData)) {
          return null;
        }

        const xLatest = getLatest(horizontalData);
        const yLatest = getLatest(verticalData);
        const skills = skillBreakdowns.get(repId);
        return {
          rep,
          coordinate: {
            x: getAxisValue(teamRange.xRange, xLatest.value),
            y: getAxisValue(teamRange.yRange, yLatest.value),
          },
          metricInfo: {
            xValueStr: axisLabel(titleMap, horizontal.metric, kpisById, xLatest.value),
            yValueStr: axisLabel(titleMap, vertical.metric, kpisById, yLatest.value),
            hasPlan: planMap.toList().some(plan => plan.recipient.id === repId && !plan.completed),
            bestSkill: skills?.bestSkill?.skillName ?? 'N/A',
            worstSkill: skills?.worstSkill?.skillName ?? 'N/A',
            coachingDuration: getRepCoachingDuration(metricData, repId),
          },
        };
      })
      // Filter out entries with no repData
      .filter(x => !!x) as RepChartPoint[]
  );
}
